import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@mui/material";
import TuneIcon from "@mui/icons-material/Tune";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import {
    buildSeries,
    COLORS,
    DEFAULT_WIDGETS,
    formatValue,
    MetricDef,
    METRICS,
    MetricSeries,
    Period,
    PERIODS,
    Viz,
    Widget,
} from "./metrics";
import "./KpiDashboard.css";

const STORAGE_KEY = "sts-kpi-dashboard";

function loadState(): { widgets: Widget[]; period: Period } {
    try {
        const raw = localStorage.getItem(STORAGE_KEY);
        if (raw) {
            const parsed = JSON.parse(raw);
            if (Array.isArray(parsed.widgets)) {
                return parsed;
            }
        }
    } catch (e) {
        // Storage unavailable or corrupted - fall back to defaults
    }
    return { widgets: DEFAULT_WIDGETS, period: "30d" };
}

export function KpiDashboard() {
    const initial = useMemo(loadState, []);
    const [widgets, setWidgets] = useState<Widget[]>(initial.widgets);
    const [period, setPeriod] = useState<Period>(initial.period);
    const [editing, setEditing] = useState(false);

    useEffect(() => {
        try {
            localStorage.setItem(STORAGE_KEY, JSON.stringify({ widgets, period }));
        } catch (e) {
            // Ignore - persistence is a convenience only
        }
    }, [widgets, period]);

    const updateWidget = (id: string, changes: Partial<Widget>) => {
        setWidgets(list => list.map(w => (w.id === id ? { ...w, ...changes } : w)));
    };

    const moveWidget = (index: number, delta: number) => {
        setWidgets(list => {
            const target = index + delta;
            if (target < 0 || target >= list.length) {
                return list;
            }
            const copy = list.slice();
            const [item] = copy.splice(index, 1);
            copy.splice(target, 0, item);
            return copy;
        });
    };

    const addWidget = () => {
        const used = widgets.map(w => w.metric);
        const metric = METRICS.find(m => used.indexOf(m.key) === -1) || METRICS[0];
        setWidgets(list => [
            ...list,
            {
                id: `w${Date.now()}`,
                metric: metric.key,
                viz: "line",
                wide: false,
                color: COLORS[list.length % COLORS.length],
            },
        ]);
    };

    const reset = () => {
        setWidgets(DEFAULT_WIDGETS);
        setPeriod("30d");
    };

    return (
        <div className="kpi">
            <div className="kpi-toolbar">
                <div>
                    <h2 className="kpi-title">Business Overview</h2>
                    <p className="kpi-subtitle">Sample data · compared with the previous {PERIODS.find(p => p.key === period)?.label}</p>
                </div>
                <div className="kpi-actions">
                    <div className="kpi-periods" role="group" aria-label="Period">
                        {PERIODS.map(p => (
                            <button
                                key={p.key}
                                className={p.key === period ? "active" : ""}
                                onClick={() => setPeriod(p.key)}
                            >
                                {p.key.toUpperCase()}
                            </button>
                        ))}
                    </div>
                    {editing && (
                        <Button size="small" startIcon={<RestartAltIcon />} onClick={reset}>
                            Reset
                        </Button>
                    )}
                    <Button
                        size="small"
                        variant={editing ? "contained" : "outlined"}
                        startIcon={editing ? <CheckIcon /> : <TuneIcon />}
                        onClick={() => setEditing(!editing)}
                    >
                        {editing ? "Done" : "Customize"}
                    </Button>
                </div>
            </div>

            <div className="kpi-grid">
                {widgets.map((widget, index) => {
                    const metric = METRICS.find(m => m.key === widget.metric) || METRICS[0];
                    return (
                        <KpiCard
                            key={widget.id}
                            widget={widget}
                            metric={metric}
                            series={buildSeries(metric, period)}
                            period={period}
                            editing={editing}
                            isFirst={index === 0}
                            isLast={index === widgets.length - 1}
                            onChange={changes => updateWidget(widget.id, changes)}
                            onMove={delta => moveWidget(index, delta)}
                            onRemove={() => setWidgets(list => list.filter(w => w.id !== widget.id))}
                        />
                    );
                })}
                {editing && (
                    <button className="kpi-add" onClick={addWidget}>
                        <AddIcon />
                        <span>Add KPI</span>
                    </button>
                )}
                {!editing && widgets.length === 0 && (
                    <div className="kpi-empty">
                        No KPIs yet. Click <strong>Customize</strong> to add some.
                    </div>
                )}
            </div>
        </div>
    );
}

interface KpiCardProps {
    widget: Widget;
    metric: MetricDef;
    series: MetricSeries;
    period: Period;
    editing: boolean;
    isFirst: boolean;
    isLast: boolean;
    onChange: (changes: Partial<Widget>) => void;
    onMove: (delta: number) => void;
    onRemove: () => void;
}

function KpiCard({ widget, metric, series, period, editing, isFirst, isLast, onChange, onMove, onRemove }: KpiCardProps) {
    const isGood = metric.higherIsBetter ? series.change >= 0 : series.change <= 0;
    const arrow = series.change >= 0 ? "▲" : "▼";

    return (
        <div
            className={`kpi-card ${widget.wide ? "wide" : ""} ${editing ? "editing" : ""}`}
            style={{ "--kpi-color": widget.color } as React.CSSProperties}
        >
            <div className="kpi-card-head">
                <span className="kpi-card-label">{metric.label}</span>
                <span className={`kpi-delta ${isGood ? "good" : "bad"}`}>
                    {arrow} {Math.abs(series.change).toFixed(1)}%
                </span>
            </div>
            <div className="kpi-card-value">{formatValue(series.value, metric.unit)}</div>
            <div className="kpi-card-prev">
                vs {formatValue(series.previous, metric.unit, true)} previous
            </div>

            <div className="kpi-chart">
                {widget.viz === "line" && <LineChart values={series.current} metric={metric} period={period} />}
                {widget.viz === "bar" && <BarChart values={series.current} metric={metric} period={period} />}
                {widget.viz === "progress" && <ProgressRing series={series} metric={metric} />}
            </div>

            {editing && (
                <div className="kpi-editor">
                    <select
                        value={widget.metric}
                        onChange={e => onChange({ metric: e.target.value })}
                        aria-label="Metric"
                    >
                        {METRICS.map(m => (
                            <option key={m.key} value={m.key}>{m.label}</option>
                        ))}
                    </select>
                    <div className="kpi-editor-row">
                        <div className="kpi-viz-switch">
                            {(["line", "bar", "progress"] as Viz[]).map(v => (
                                <button
                                    key={v}
                                    className={widget.viz === v ? "active" : ""}
                                    onClick={() => onChange({ viz: v })}
                                >
                                    {v}
                                </button>
                            ))}
                        </div>
                        <label className="kpi-wide-toggle">
                            <input
                                type="checkbox"
                                checked={widget.wide}
                                onChange={e => onChange({ wide: e.target.checked })}
                            />
                            Wide
                        </label>
                    </div>
                    <div className="kpi-editor-row">
                        <div className="kpi-colors">
                            {COLORS.map(c => (
                                <button
                                    key={c}
                                    className={widget.color === c ? "active" : ""}
                                    style={{ background: c }}
                                    onClick={() => onChange({ color: c })}
                                    aria-label={`Color ${c}`}
                                />
                            ))}
                        </div>
                        <div className="kpi-editor-icons">
                            <button disabled={isFirst} onClick={() => onMove(-1)} aria-label="Move left">
                                <ChevronLeftIcon fontSize="small" />
                            </button>
                            <button disabled={isLast} onClick={() => onMove(1)} aria-label="Move right">
                                <ChevronRightIcon fontSize="small" />
                            </button>
                            <button className="danger" onClick={onRemove} aria-label="Remove">
                                <DeleteOutlineIcon fontSize="small" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

interface ChartProps {
    values: number[];
    metric: MetricDef;
    period: Period;
}

const CHART_W = 300;
const CHART_H = 90;

function useHover() {
    const [hover, setHover] = useState<number | null>(null);
    const handleMove = (e: React.MouseEvent<SVGSVGElement>, count: number) => {
        const rect = e.currentTarget.getBoundingClientRect();
        const ratio = (e.clientX - rect.left) / rect.width;
        setHover(Math.max(0, Math.min(count - 1, Math.round(ratio * (count - 1)))));
    };
    return { hover, handleMove, clear: () => setHover(null) };
}

function Tooltip({ index, values, metric, period, x }: { index: number; values: number[]; metric: MetricDef; period: Period; x: number }) {
    const info = PERIODS.find(p => p.key === period) || PERIODS[1];
    return (
        <div className="kpi-tooltip" style={{ left: `${x}%` }}>
            <span>{info.pointLabel} {index + 1}</span>
            <strong>{formatValue(values[index], metric.unit)}</strong>
        </div>
    );
}

function LineChart({ values, metric, period }: ChartProps) {
    const { hover, handleMove, clear } = useHover();
    const max = Math.max(...values);
    const min = Math.min(...values);
    const range = max - min || 1;
    const points = values.map((v, i) => [
        (i / (values.length - 1)) * CHART_W,
        CHART_H - 6 - ((v - min) / range) * (CHART_H - 16),
    ]);
    const line = points.map((p, i) => `${i === 0 ? "M" : "L"}${p[0].toFixed(1)},${p[1].toFixed(1)}`).join(" ");
    const area = `${line} L${CHART_W},${CHART_H} L0,${CHART_H} Z`;
    const gradientId = `kpi-grad-${metric.key}-${period}`;

    return (
        <div className="kpi-chart-wrap">
            <svg
                viewBox={`0 0 ${CHART_W} ${CHART_H}`}
                preserveAspectRatio="none"
                onMouseMove={e => handleMove(e, values.length)}
                onMouseLeave={clear}
            >
                <defs>
                    <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                        <stop offset="0%" stopColor="var(--kpi-color)" stopOpacity="0.28" />
                        <stop offset="100%" stopColor="var(--kpi-color)" stopOpacity="0" />
                    </linearGradient>
                </defs>
                <path d={area} fill={`url(#${gradientId})`} />
                <path d={line} fill="none" stroke="var(--kpi-color)" strokeWidth="2.5" vectorEffect="non-scaling-stroke" strokeLinejoin="round" />
                {hover !== null && (
                    <line x1={points[hover][0]} x2={points[hover][0]} y1="0" y2={CHART_H} stroke="var(--kpi-color)" strokeOpacity="0.4" vectorEffect="non-scaling-stroke" strokeDasharray="3 3" />
                )}
            </svg>
            {hover !== null && (
                <>
                    <span
                        className="kpi-dot"
                        style={{ left: `${(points[hover][0] / CHART_W) * 100}%`, top: `${(points[hover][1] / CHART_H) * 100}%` }}
                    />
                    <Tooltip index={hover} values={values} metric={metric} period={period} x={(points[hover][0] / CHART_W) * 100} />
                </>
            )}
        </div>
    );
}

function BarChart({ values, metric, period }: ChartProps) {
    const { hover, handleMove, clear } = useHover();
    const max = Math.max(...values) || 1;
    const slot = CHART_W / values.length;
    const barWidth = Math.max(2, slot * 0.62);

    return (
        <div className="kpi-chart-wrap">
            <svg
                viewBox={`0 0 ${CHART_W} ${CHART_H}`}
                preserveAspectRatio="none"
                onMouseMove={e => handleMove(e, values.length)}
                onMouseLeave={clear}
            >
                {values.map((v, i) => {
                    const h = (v / max) * (CHART_H - 8);
                    return (
                        <rect
                            key={i}
                            x={i * slot + (slot - barWidth) / 2}
                            y={CHART_H - h}
                            width={barWidth}
                            height={h}
                            rx={Math.min(3, barWidth / 3)}
                            fill="var(--kpi-color)"
                            opacity={hover === null || hover === i ? 0.9 : 0.35}
                        />
                    );
                })}
            </svg>
            {hover !== null && (
                <Tooltip index={hover} values={values} metric={metric} period={period} x={((hover + 0.5) / values.length) * 100} />
            )}
        </div>
    );
}

function ProgressRing({ series, metric }: { series: MetricSeries; metric: MetricDef }) {
    // For "lower is better" metrics the goal is to stay under the target
    const ratio = metric.higherIsBetter ? series.value / series.target : series.target / series.value;
    const pct = Math.max(0, Math.min(1, ratio));
    const radius = 34;
    const circumference = 2 * Math.PI * radius;

    return (
        <div className="kpi-progress">
            <svg viewBox="0 0 90 90" width="90" height="90">
                <circle cx="45" cy="45" r={radius} fill="none" stroke="#ede9fe" strokeWidth="9" />
                <circle
                    cx="45"
                    cy="45"
                    r={radius}
                    fill="none"
                    stroke="var(--kpi-color)"
                    strokeWidth="9"
                    strokeLinecap="round"
                    strokeDasharray={`${circumference * pct} ${circumference}`}
                    transform="rotate(-90 45 45)"
                    className="kpi-progress-arc"
                />
                <text x="45" y="50" textAnchor="middle" className="kpi-progress-text">
                    {Math.round(ratio * 100)}%
                </text>
            </svg>
            <div className="kpi-progress-info">
                <span>Target</span>
                <strong>{formatValue(series.target, metric.unit)}</strong>
                <small>{metric.higherIsBetter ? "15% above previous period" : "15% below previous period"}</small>
            </div>
        </div>
    );
}
